import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Form, Modal, Table, Card } from "react-bootstrap";
import Cookies from "js-cookie";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToasts } from "react-toast-notifications";
import { SHOP_APP_V2 } from "../../graphql/shop/queryShop";
import { UPDATE_SHOP } from "../../graphql/shop/mutation";

function SettingAppV2() {
    const { addToast } = useToasts();
    const shopId = useMemo(() => {
        try {
        return JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
        } catch (e) {
        return undefined;
        }
    }, []);

    const [pages, setPages] = useState([]); // [{pageId,name,token,status}]
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedToAdd, setSelectedToAdd] = useState("");
    const [search, setSearch] = useState("");
    const isTokenExpired = useCallback((dateStr) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return false;
        return d.getTime() < Date.now();
    }, []);

    // Load shop pages from API
    const [getShop, { data: shopData, loading: loadingShop }] = useLazyQuery(
        SHOP_APP_V2,
        { fetchPolicy: "network-only" }
    );

    const [updateShop, { loading: updating }] = useMutation(UPDATE_SHOP);

    useEffect(() => {
        if (!shopId) return;
        getShop({
        variables: {
            where: { id: shopId },
        },
        });
    }, [shopId, getShop]);

    useEffect(() => {
        if (shopData?.shop?.pages) {
        // Normalize server data: use _id internally. Backward-compat if server still returns pageId
        setPages(
            shopData.shop.pages.map((p) => ({
            _id: p?._id ?? p?.pageId,
            name: p?.name,
            token: p?.token,
            status: p?.status,
            createdAt: p?.createdAt,
            updatedAt: p?.updatedAt,
            expiryDate: p?.expiryDate,
            }))
        );
        }
    }, [shopData]);

    // Available FB pages from cookie (same as PostFeedAdd.js)
    const assignedPageList = useMemo(() => {
        const shopPageCookie = Cookies.get("SHOP_PAGE");
        try {
        return shopPageCookie ? JSON.parse(shopPageCookie) : [];
        } catch (e) {
        return [];
        }
    }, []);

    const existingPageIds = useMemo(
        () => new Set((pages || []).map((p) => String(p?._id))),
        [pages]
    );
    const candidateAddPages = useMemo(() => {
        const list = Array.isArray(assignedPageList) ? assignedPageList : [];
        return list
        .filter((p) => !existingPageIds.has(String(p?.id)))
        .map((p) => ({ id: String(p?.id), name: p?.name, token: p?.access_token }));
    }, [assignedPageList, existingPageIds]);

    const handleToggleStatus = (_id) => {
        setPages((prev) => prev.map((p) => (p._id === _id ? { ...p, status: !p.status } : p)));
    };

    const handleRemove = (_id) => {
        setPages((prev) => prev.filter((p) => p._id !== _id));
    };

    const handleOpenAdd = () => setShowAddModal(true);
    const handleCloseAdd = () => {
        setSelectedToAdd("");
        setShowAddModal(false);
    };

    const handleAddPage = () => {
        if (!selectedToAdd) return;
        const found = candidateAddPages.find((p) => p.id === selectedToAdd);
        if (!found) return;
        const nowIso = new Date().toISOString();
        const expiry90DaysIso = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
        setPages((prev) => [
        ...prev,
        {
            _id: found.id,
            name: found.name,
            token: found.token,
            status: true,
            createdAt: nowIso,
            updatedAt: nowIso,
            // default expiry in 90 days if token exists
            expiryDate: found.token ? expiry90DaysIso : null,
        },
        ]);
        handleCloseAdd();
    };

    const handleSave = async () => {
        if (!shopId) return;
        try {
        await updateShop({
            variables: {
            data: {
                pages: pages.map((p) => ({
                _id: p._id,
                name: p.name,
                token: p.token,
                status: Boolean(p.status),
                expiryDate: p.expiryDate || null,
                createdAt: p.createdAt || null,
                updatedAt: new Date().toISOString(),
                })),
            },
            where: { id: shopId },
            },
        });
        addToast("ບັນທຶກການຕັ້ງຄ່າໜ້າເຟສບຸກສໍາເລັດ", {
            appearance: "success",
            autoDismiss: true,
        });
        // refresh to ensure state from server
        getShop({ variables: { where: { id: shopId } } });
        } catch (e) {
        addToast("ເກີດຂໍ້ຜິດພາດໃນການບັນທຶກ", {
            appearance: "error",
            autoDismiss: true,
        });
        }
    };

    return (
        <div className="container p-3">
        <Card className="shadow-sm">
            <Card.Body>
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                <div>
                <h5 className="m-0">APP V2 - ຈັດການເພຈ Facebook</h5>
                <div style={{ color: "#6c757d", fontSize: 14 }}>ເພີ່ມ/ປິດເປີດໜ້າ ແລະ ບັນທຶກການປ່ຽນແປງ</div>
                </div>
                <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={handleOpenAdd}>
                    + ເພີ່ມເພຈ
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={updating || loadingShop}>
                    {updating ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
                </Button>
                </div>
            </div>

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
                <Form.Control
                placeholder="ຄົ້ນຫາຊື່ເພຈ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 320 }}
                />
                <div style={{ color: "#6c757d", fontSize: 13 }}>
                {candidateAddPages.length === 0
                    ? "ບໍ່ມີເພຈໃຫມ່ໃນຄຸກກີ້. ກະລຸນາເຊື່ອມຕໍ່ Facebook ກ່ອນ"
                    : "ເລືອກເພື່ອເພີ່ມເຂົ້າຮ້ານຄ້າຂອງທ່ານ."}
                </div>
            </div>

            <Table hover responsive className="mb-0">
                <thead>
                <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>ຊື່ເພຈ</th>
                    <th style={{ width: 180 }}>ສະຖານະ</th>
                    <th style={{ width: 120 }}>ເພີ່ມວັນທີ່</th>
                    <th style={{ width: 120 }}>ອັບເດດວັນທີ່</th>
                    <th style={{ width: 120 }}>ຈັດການ</th>
                </tr>
                </thead>
                <tbody>
                {(pages || [])
                    .filter((p) =>
                    search ? (p?.name || "").toLowerCase().includes(search.toLowerCase()) : true
                    )
                    .map((p, idx) => (
                    <tr key={p._id} className={!p?.token || isTokenExpired(p?.expiryDate) ? "table-danger" : ""}>
                        <td>{idx + 1}</td>
                        <td className="d-flex align-items-center gap-2">
                        <div
                            style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            background: "#E8F0FE",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            }}
                        >
                            f
                        </div>
                        <div>
                            <div style={{ fontWeight: 600 }}>{p.name}</div>
                            <div style={{ color: "#6c757d", fontSize: 12 }}>{String(p._id).slice(0, 5)}...</div>
                        </div>
                        </td>
                        <td>
                        <div className="d-flex align-items-center">
                            <Form.Check
                            type="switch"
                            id={`status-${p._id}`}
                            checked={Boolean(p.status)}
                            onChange={() => handleToggleStatus(p._id)}
                            />
                            <span style={{ marginLeft: 8, fontSize: 12, color: !p?.token || isTokenExpired(p?.expiryDate) ? "#dc3545" : "#6c757d" }}>
                                {!p?.token
                                ? "No token"
                                : isTokenExpired(p?.expiryDate)
                                ? `Expired${p?.expiryDate ? ` (${new Date(p.expiryDate).toLocaleDateString()})` : ""}`
                                : p?.expiryDate
                                ? `Expires ${new Date(p.expiryDate).toLocaleDateString()}`
                                : ""}
                            </span>
                        </div>
                        </td>
                        <td>{p?.createdAt ? new Date(p.createdAt).toLocaleDateString() : ""}</td>
                        <td>{p?.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : ""}</td>
                        <td>
                        <Button variant="outline-danger" size="sm" onClick={() => handleRemove(p._id)}>
                            ລົບ
                        </Button>
                        </td>
                    </tr>
                    ))}
                {(!pages || pages.length === 0) && (
                    <tr>
                    <td colSpan={6} className="text-center p-4">
                        {loadingShop ? "ກຳລັງໂຫຼດ..." : "ຍັງບໍ່ມີເພຈ"}
                    </td>
                    </tr>
                )}
                </tbody>
            </Table>
            </Card.Body>
        </Card>

        {/* Add Page Modal */}
        <Modal show={showAddModal} onHide={handleCloseAdd} centered>
            <Modal.Header closeButton>
            <Modal.Title>ເພີ່ມເພຈຈາກ Facebook</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            {candidateAddPages.length > 0 ? (
                <Form.Control
                as="select"
                value={selectedToAdd}
                onChange={(e) => setSelectedToAdd(e.target.value)}
                >
                <option value="">ເລືອກເພຈ</option>
                {candidateAddPages.map((p) => (
                    <option key={p.id} value={p.id}>
                    {p.name}
                    </option>
                ))}
                </Form.Control>
            ) : (
                <div className="w-100 p-3 text-center">
                ບໍ່ມີເພຈໃຫມ່ໃນຄຸກກີ້ SHOP_PAGE
                </div>
            )}
            </Modal.Body>
            <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAdd}>
                ຍົກເລີກ
            </Button>
            <Button variant="primary" onClick={handleAddPage} disabled={!selectedToAdd}>
                ເພີ່ມ
            </Button>
            </Modal.Footer>
        </Modal>
        </div>
    );
}

export default SettingAppV2;

